import { SapPrintInfosService } from '@/api/bff';
import { SapProduct, SapProductState } from '@/context/SapProduct/types';
import {
  deleteRecords,
  getRecord,
  isExpired,
  ProductIDB,
  saveRecords,
} from '@/services/indexeddb';
import { StoreWithPrinters } from '@/types/store';
import React, { createContext, useContext, useState } from 'react';

type SapProductContextType = SapProductState & {
  fetchSapProducts: (
    upcs: string[],
    store: StoreWithPrinters
  ) => Promise<Record<string, SapProduct>>;
  clear: () => void;
};

const SapProductContext = createContext<SapProductContextType | undefined>(
  undefined
);

const buildKey = (
  upc: string,
  storeCode: string,
  salesOrganization: string,
  distributionChannel: string
) => `${upc}_${storeCode}_${salesOrganization}_${distributionChannel}`;

export const SapProductProvider: React.FC = ({ children }) => {
  const [state, setState] = useState<SapProductState>({
    sapData: {},
    status: 'idle',
    error: null,
  });

  const clear = (): void => {
    setState({
      sapData: {},
      status: 'idle',
      error: null,
    });
  };

  const fetchSapProducts = async (
    upcs: string[],
    store: StoreWithPrinters
  ): Promise<Record<string, SapProduct>> => {
    try {
      setState(prevState => ({
        ...prevState,
        status: 'loading',
      }));

      const storeCode = store.storeCode ?? '';
      const distributionChannel = store.distributionChannel ?? '';
      const salesOrganization = store.salesOrganization ?? '';

      // 1) Leggi IDB in parallelo
      const idbEntries = await Promise.all(
        upcs.map(async upc => {
          const key = buildKey(
            upc,
            storeCode,
            salesOrganization,
            distributionChannel
          );

          const rec = await getRecord(key);
          if (!rec) {
            return {
              upc,
              rec: undefined as ProductIDB | undefined,
              expired: false,
            };
          }

          const expired = isExpired(rec);
          return { upc, rec: expired ? undefined : rec, expired };
        })
      );

      // 2) Cancella gli scaduti (in parallelo)
      const expiredUpcs = idbEntries.reduce((acc: string[], el) => {
        if (el.expired) {
          acc.push(el.upc);
        }
        return acc;
      }, []);

      if (expiredUpcs.length) {
        const expiredKeys = idbEntries.map(e =>
          buildKey(e.upc, storeCode, salesOrganization, distributionChannel)
        );
        await deleteRecords(expiredKeys); // transaction unica
      }

      // 3) Split diretto
      const cachedValid: ProductIDB[] = [];
      const missingUpcs: string[] = [];

      for (const e of idbEntries) {
        if (e.rec) cachedValid.push(e.rec);
        else missingUpcs.push(e.upc);
      }

      // 4) Se non manca nulla, evita chiamata BFF
      let fromSap: ProductIDB[] = [];
      if (missingUpcs.length > 0) {
        const res = await SapPrintInfosService.sapPrintInfos({
          requestBody: {
            storeCode,
            distributionChannel,
            salesOrganization,
            upcs: missingUpcs,
          },
        });

        // costruzione oggetto per idb, verifica che status !== "NOT_FOUND"
        fromSap = (res?.prescriptions ?? []).reduce<ProductIDB[]>((acc, el) => {
          if (el.status !== 'NOT_FOUND') {
            const id = buildKey(
              el.upc,
              storeCode,
              salesOrganization,
              distributionChannel
            );

            acc.push({
              id,
              upc: el.upc,
              rxvalue: el.rxvalue || null,
              prescription: el.prescription || null,
              store: storeCode,
              salesOrganization: salesOrganization,
              distributionChannel: distributionChannel,
              timestamp: Date.now(),
            });
          }

          return acc;
        }, []);

        if (fromSap.length > 0) {
          await saveRecords(fromSap); // transaction unica
        }
      }

      // 5) Unisci array
      const all = [...cachedValid, ...fromSap];

      // 6) Costruisci results
      const contextResults: Record<string, SapProduct> = {};
      for (const product of all) {
        contextResults[product.upc] = {
          upc: product.upc,
          prescription: product.prescription ?? '',
          rxvalue: product.rxvalue,
        };
      }

      setState(prev => ({
        ...prev,
        sapData: {
          ...prev.sapData,
          ...contextResults,
        },
        status: 'success',
        error: null,
      }));

      return contextResults;
    } catch (err) {
      setState(prev => ({
        ...prev,
        status: 'error',
        error: {
          code: err?.status ?? 500,
          message: err?.message ?? 'Unexpected error',
        },
      }));
      return {};
    }
  };

  return (
    <SapProductContext.Provider
      value={{
        ...state,
        fetchSapProducts,
        clear,
      }}
    >
      {children}
    </SapProductContext.Provider>
  );
};

export const useSapProductContext = (): SapProductContextType => {
  const context = useContext(SapProductContext);
  if (!context) {
    throw new Error('useSapPrint must be used within SapPrintProvider');
  }
  return context;
};
